import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AddBranch } from "../components/branches/add-branch";
import { BranchItem } from "../components/branches/branch-item";
import { Loading } from "../components/Loading";
import { PaycloudBranch } from "../types";
import { BranchService } from "../services/branch-service";
import { useToast } from "../context/toast-context";
import { useTranslation } from "react-i18next";
import { ScheduleRequestDto } from "../services/dto/response/schedule-response-dto";
import { useUser } from "../context/user-context";
import { useLocationPreload } from "../hooks/useLocationPreload";
import { useInfinityScroll } from "../hooks/useInfinityScroll";
import { InfinityScrollLoader } from "../components/InfinityScrollLoader";

const ITEMS_PER_PAGE = 20;

// Sucursales
export function Branches() {
  const [branches, setBranches] = useState<PaycloudBranch[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);
  const branchService = useMemo(() => new BranchService(), []);
  const { showToast } = useToast();
  const { t } = useTranslation();
  const { user, isLoading: isLoadingUser } = useUser();
  // Evita que el observer dispare dos páginas en el mismo frame
  const isFetchingRef = useRef(false);

  // Precargar datos de ubicación cuando se entra a la pantalla de sucursales
  useLocationPreload();

  const isSuperManager = user?.rol === "Manager" && user?.isResponsible;

  // El formulario de alta ya recargó en el back, acá reiniciamos el scroll infinito
  const handleAddBranch = (_newBranch: PaycloudBranch[]) => {
    setBranches([]);
    setHasMore(true);
    setPage(0);
    setReloadToken((prev) => prev + 1);
  };

  const handleDeleteBranch = (branchId: number) => {
    setBranches((prev) =>
      prev.filter((branch) => branch.branchId !== branchId)
    );
  };

  const handleUpdateBranch = (updatedBranch: PaycloudBranch[]) => {
    setBranches((prev) =>
      prev.map((branch) => {
        const updated = updatedBranch.find(ub => ub.branchId === branch.branchId);
        if (updated) {
          // Mantener el employeeCount porque la respuesta del back no la contiene
          return {
            ...updated,
            employeeCount: branch.employeeCount,
          };
        }
        return branch;
      })
    );
  };

  const handleAddEmployee = (branchId: number, amountAdded: number) => {
    setBranches((prev) =>
      prev.map((branch) =>
        branch.branchId === branchId
          ? {
              ...branch,
              employeeCount: Number(branch.employeeCount) + amountAdded,
            }
          : branch
      )
    );
  };

  const handleAddSchedule = (branchId: number, schedules: ScheduleRequestDto[]) => {
  };

  const handleToggleManualProcess = (branchId: number, newValue: boolean) => {
    setBranches((prev) =>
      prev.map((branch) =>
        branch.branchId === branchId
          ? { ...branch, manualTipsProccess: newValue }
          : branch
      )
    );
  };

  const handleUnlinkClover = (branchId: number) => {
    setBranches((prev) =>
      prev.map((branch) =>
        branch.branchId === branchId
          ? {
              ...branch,
              cloverExternalId: undefined,
              wasAuthorized: false,
              origins: (branch.origins || []).includes("DIVIX")
                ? (branch.origins || []).filter((origin) => origin !== "CLOVER")
                : branch.origins,
            }
          : branch
      )
    );
  };

  const handleToggleCloverPaymentEmployee = (branchId: number, newValue: boolean) => {
    setBranches((prev) =>
      prev.map((branch) =>
        branch.branchId === branchId
          ? { ...branch, considerPaymentEmployee: newValue }
          : branch
      )
    );
  };

  const fetchBranches = useCallback(
    async (pageToFetch: number, isAborted: () => boolean) => {
      isFetchingRef.current = true;
      setIsLoading(true);

      try {
        await branchService.getBranches(
          { limit: ITEMS_PER_PAGE, offset: pageToFetch * ITEMS_PER_PAGE },
          (data) => {
            if (isAborted()) return;

            setBranches((prev) => {
              if (pageToFetch === 0) return data.branches;

              const alreadyLoaded = new Set(prev.map((b) => b.branchId));
              return [
                ...prev,
                ...data.branches.filter((b) => !alreadyLoaded.has(b.branchId)),
              ];
            });
            setHasMore(data.branches.length === ITEMS_PER_PAGE);
          },
          (errorMessage) => {
            if (!isAborted()) {
              showToast(errorMessage, "error");
            }
          }
        );
      } finally {
        if (!isAborted()) {
          isFetchingRef.current = false;
          setIsLoading(false);
        }
      }
    },
    [branchService, showToast]
  );

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    fetchBranches(page, () => signal.aborted);

    return () => {
      controller.abort();
    };
  }, [fetchBranches, page, reloadToken]);

  const loadMore = useCallback(() => {
    if (isFetchingRef.current || isLoading || !hasMore) return;

    isFetchingRef.current = true;
    setPage((prev) => prev + 1);
  }, [isLoading, hasMore]);

  const lastBranchRef = useInfinityScroll({
    loadMore,
    hasMore,
    loading: isLoading,
  });

  return (
    <main className="h-full flex flex-col rounded-3xl bg-[#a0abba] p-6 overflow-hidden">
      {/* Contenido encabezado */}
      <div className="mb-4 flex justify-end">
        <AddBranch onAddBranch={handleAddBranch} />
      </div>

      {/* Contenido principal */}
      <div className="flex flex-col flex-1 gap-2 h-full overflow-y-auto custom-scrollbar rounded-lg p-4">
        {((isLoading && page === 0) || isLoadingUser) && <Loading />}
        {!isLoading && !isLoadingUser && branches.length === 0 && (
          <div className="w-full h-full flex justify-center items-center text-white text-2xl">
            <p>{t("branches.no-branches")}</p>
          </div>
        )}
        {!isLoadingUser &&
          branches.length > 0 &&
          branches.map((branch, index) => (
            <BranchItem
              onUpdateBranch={handleUpdateBranch}
              onDeleteBranch={handleDeleteBranch}
              onAddEmployee={handleAddEmployee}
              onAddSchedule={handleAddSchedule}
              onToggleManualProcess={handleToggleManualProcess}
              onUnlinkClover={handleUnlinkClover}
              onToggleCloverPaymentEmployee={handleToggleCloverPaymentEmployee}
              branch={branch}
              isSuperManager={isSuperManager}
              key={branch.branchId}
            />
          ))}
        {!isLoadingUser && branches.length > 0 && hasMore && (
          <div ref={lastBranchRef} className="h-1 w-full shrink-0" />
        )}
        {isLoading && page > 0 && <InfinityScrollLoader />}
      </div>
    </main>
  );
}
